import { createContext, type ReactNode, useCallback, useEffect, useState } from "react";
import { LiveRegion } from "@/components/a11y";

/**
 * A11yContext
 * Provides global accessibility features including:
 * - Live region announcements for screen readers
 * - Accessibility preferences management
 */

interface AnnouncementState {
	message: string;
	politeness: "polite" | "assertive";
	timestamp: number;
}

interface A11yContextValue {
	/**
	 * Announce a message to screen readers
	 * @param message - Text to announce
	 * @param politeness - Priority level ('polite' or 'assertive')
	 */
	announce: (message: string, politeness?: "polite" | "assertive") => void;
}

export const A11yContext = createContext<A11yContextValue | null>(null);

interface A11yProviderProps {
	children: ReactNode;
}

export function A11yProvider({ children }: A11yProviderProps) {
	const [politeAnnouncement, setPoliteAnnouncement] = useState<AnnouncementState | null>(null);
	const [assertiveAnnouncement, setAssertiveAnnouncement] = useState<AnnouncementState | null>(
		null,
	);

	const announce = useCallback((message: string, politeness: "polite" | "assertive" = "polite") => {
		const announcement: AnnouncementState = {
			message,
			politeness,
			timestamp: Date.now(),
		};

		if (politeness === "assertive") {
			setAssertiveAnnouncement(announcement);
		} else {
			setPoliteAnnouncement(announcement);
		}
	}, []);

	// Clear announcements after they've been read
	useEffect(() => {
		if (politeAnnouncement) {
			const timer = setTimeout(() => {
				setPoliteAnnouncement(null);
			}, 1000);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [politeAnnouncement]);

	useEffect(() => {
		if (assertiveAnnouncement) {
			const timer = setTimeout(() => {
				setAssertiveAnnouncement(null);
			}, 1000);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [assertiveAnnouncement]);

	return (
		<A11yContext.Provider value={{ announce }}>
			{children}

			{/* Global live regions */}
			<LiveRegion politeness="polite">{politeAnnouncement?.message}</LiveRegion>

			<LiveRegion politeness="assertive">{assertiveAnnouncement?.message}</LiveRegion>
		</A11yContext.Provider>
	);
}
